import { PUBLIC_SWS_ENV } from '@/constants/env';
import {
  ROUTE_COMPANY,
  ROUTE_COMPANY_WITH_EVENTS,
  ROUTE_LOCALE_COMPANY,
  ROUTE_LOCALE_COMPANY_WITH_EVENTS,
  ROUTE_USER_PROFILE,
  ROUTE_USER_NOTIFICATIONS,
  ROUTE_USER_SUBSCRIPTION_AND_BILLING,
  ROUTE_DISCOVER_INVESTING_IDEAS,
  ROUTE_STOCKS_MARKET,
  ROUTE_DISCOVER_INVESTING_IDEAS_MARKET,
  ROUTE_INVESTING_IDEA_HOME,
  ROUTE_STOCKS_HOME,
  ROUTE_STOCKS,
  ROUTE_INVESTING_IDEA_MARKET_INDUSTRY,
  ROUTE_INVESTING_IDEA_MARKET,
  ROUTE_STOCKS_MARKET_INDUSTRY,
} from '@/constants/routes';
import {
  hasSalesSiteTranslation,
  isExperimental,
  isSEOIndexable,
  LOCALE_ENABLED_WEB,
  LOCALES,
  type Locale as AllLocales,
} from '@simplywallst/locale';

const TRANSLATED_PAGES = [
  ROUTE_COMPANY,
  ROUTE_COMPANY_WITH_EVENTS,
  ROUTE_LOCALE_COMPANY,
  ROUTE_LOCALE_COMPANY_WITH_EVENTS,
  ROUTE_USER_SUBSCRIPTION_AND_BILLING,
  ROUTE_USER_PROFILE,
  ROUTE_USER_NOTIFICATIONS,
  ROUTE_STOCKS_HOME,
  ROUTE_STOCKS,
  ROUTE_STOCKS_MARKET,
  ROUTE_STOCKS_MARKET_INDUSTRY,
  ROUTE_INVESTING_IDEA_HOME,
  ROUTE_INVESTING_IDEA_MARKET,
  ROUTE_INVESTING_IDEA_MARKET_INDUSTRY,
  ROUTE_DISCOVER_INVESTING_IDEAS,
  ROUTE_DISCOVER_INVESTING_IDEAS_MARKET,
] as const;

export const isPageTranslated = (type: string) => {
  // Remove the locale suffix from the type if it exists.
  // Can presume that if the route is a locale route of the base translated route, it's a translated
  // page.
  const routeType = type.replace(
    '-LOCALE',
    ''
  ) as (typeof TRANSLATED_PAGES)[number];
  return TRANSLATED_PAGES.includes(routeType);
};

/**
 * Route action types which are 'enabled' to handle locale as a route param.
 *
 * e.g. /:locale/stocks/...
 */
const LOCALE_ROUTE_TYPES = [
  ROUTE_LOCALE_COMPANY,
  ROUTE_LOCALE_COMPANY_WITH_EVENTS,
] as const;
type LocaleRouteType = (typeof LOCALE_ROUTE_TYPES)[number];

/**
 * Determine whether a redux action type is one that supports locale in the url params.
 */
export const isLocaleRouteType = (
  actionType: string | LocaleRouteType
): actionType is LocaleRouteType => {
  return LOCALE_ROUTE_TYPES.includes(actionType as LocaleRouteType);
};

function getLocales() {
  if (PUBLIC_SWS_ENV === 'prod') {
    /**
     * only return locales which are enabled on production
     */
    return LOCALE_ENABLED_WEB;
  } else {
    /**
     * always return true for non-prod environments
     */
    return LOCALES;
  }
}

/**
 * Available route locale language translations.
 *
 * Note: `en` is valid as a language, but not in routing / url values. These use the 'default' non-locale routes instead.
 */
export const SUPPORTED_LOCALES = getLocales();

export type Locale = AllLocales;

export const isSupportedLocale = (
  localeValue?: string | Locale
): localeValue is Locale => {
  return SUPPORTED_LOCALES.includes(
    localeValue as ReturnType<typeof getLocales>[number]
  );
};

export const isTranslationExperimental = (locale: Locale) => {
  return isExperimental(locale, 'web');
};

export const isLocaleCrawlable = (locale: Locale) => {
  return isSEOIndexable(locale, 'web');
};

export const isPageSupported = (
  type: string,
  locale: string
): locale is Locale => {
  if (
    isPageTranslated(type) &&
    (locale === 'en' || isSupportedLocale(locale))
  ) {
    return true;
  }
  return false;
};

export const SUPPORTED_NAMESPACES = [
  'web',
  'statementGlossary',
  'page.companyReport',
  'page.companyReport.dividend',
  'page.companyReport.future',
  'page.companyReport.past',
  'page.companyReport.overview',
  'page.companyReport.health',
  'page.companyReport.management',
  'page.companyReport.ownership',
  'page.companyReport.information',
  'page.companyReport.valuation',
  'page.discover',
  'page.stocks',
  'page.plans',
  'discover',
] as const;

export const isSalesSiteTranslated = (localeValue: Locale): boolean => {
  return hasSalesSiteTranslation(localeValue);
};
